import React from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

const normalizeId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value._id || value.id || value.name || '';
};

const normalizeName = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.name || value.label || value._id || value.id || '';
};

const getArticleTagKeys = (article = {}) => {
  const tags = Array.isArray(article.tags) ? article.tags : [];
  return tags.reduce((acc, tag) => {
    const id = normalizeId(tag);
    const name = normalizeName(tag);
    if (id) acc.add(String(id));
    if (name) acc.add(String(name));
    return acc;
  }, new Set());
};

export const filterNewsletterArticlesByTags = (articles = [], config = {}, availableTags = []) => {
  if (!config.enabled || !Array.isArray(config.tagIds) || config.tagIds.length === 0) {
    return articles;
  }

  const selectedKeys = new Set();
  const tagById = new Map(availableTags.map(tag => [String(normalizeId(tag)), tag]));

  config.tagIds.forEach(tagId => {
    const rawId = String(tagId);
    const tag = tagById.get(rawId);
    selectedKeys.add(rawId);
    if (tag?.name) selectedKeys.add(String(tag.name));
  });

  return articles.filter(article => {
    const articleTagKeys = getArticleTagKeys(article);
    const matches = [...selectedKeys].some(key => articleTagKeys.has(key));
    return config.mode === 'include' ? matches : !matches;
  });
};

const orderTagsByTemplate = (tags = [], tagsOrder = []) => {
  if (!Array.isArray(tagsOrder) || tagsOrder.length === 0) return tags;
  const orderMap = new Map(tagsOrder.map((name, index) => [String(name), index]));

  return [...tags].sort((a, b) => {
    const aIndex = orderMap.has(a.name) ? orderMap.get(a.name) : Number.MAX_SAFE_INTEGER;
    const bIndex = orderMap.has(b.name) ? orderMap.get(b.name) : Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
  });
};

export const buildNewsletterTagFilterPayload = (config = {}, availableTags = [], tagsOrder = []) => {
  if (!config.enabled || !Array.isArray(config.tagIds) || config.tagIds.length === 0) {
    return {
      enabled: false,
      mode: 'exclude',
      tagIds: [],
      tagNames: []
    };
  }

  const tagById = new Map(availableTags.map(tag => [String(normalizeId(tag)), tag]));
  const tagIds = config.tagIds.map(tagId => String(tagId));
  const selectedTags = orderTagsByTemplate(
    tagIds
      .map(tagId => tagById.get(tagId))
      .filter(Boolean),
    tagsOrder
  );

  return {
    enabled: true,
    mode: config.mode === 'include' ? 'include' : 'exclude',
    tagIds: selectedTags.map(tag => String(normalizeId(tag))),
    tagNames: selectedTags.map(tag => tag.name).filter(Boolean)
  };
};

const NewsletterTagFilterControl = ({
  articles,
  availableTags,
  value,
  onChange,
  compact = false,
  tagsOrder = []
}) => {
  const orderedAvailableTags = React.useMemo(
    () => orderTagsByTemplate(availableTags, tagsOrder),
    [availableTags, tagsOrder]
  );
  const selectedTags = orderTagsByTemplate(
    availableTags.filter(tag => value.tagIds.includes(String(normalizeId(tag)))),
    tagsOrder
  );
  const filteredArticles = filterNewsletterArticlesByTags(articles, value, availableTags);
  const removedCount = Math.max(articles.length - filteredArticles.length, 0);

  const patchValue = (patch) => {
    onChange({
      ...value,
      ...patch
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 1.5 : 2,
        borderRadius: 2,
        bgcolor: 'background.default'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: value.enabled ? 1.5 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterAltIcon fontSize="small" color="primary" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Filtro por tags no envio
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Define quais artigos entram na newsletter final.
            </Typography>
          </Box>
        </Box>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              size="small"
              checked={value.enabled}
              onChange={(event) => patchValue({ enabled: event.target.checked })}
            />
          }
          label={value.enabled ? 'Ativo' : 'Inativo'}
        />
      </Box>

      {value.enabled && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: compact ? '1fr' : '170px 1fr' }, gap: 1.25 }}>
          <TextField
            select
            size="small"
            label="Modo"
            value={value.mode}
            onChange={(event) => patchValue({ mode: event.target.value })}
          >
            <MenuItem value="exclude">Excluir tags</MenuItem>
            <MenuItem value="include">Enviar apenas tags</MenuItem>
          </TextField>

          <Autocomplete
            multiple
            size="small"
            options={orderedAvailableTags}
            value={selectedTags}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, selected) => normalizeId(option) === normalizeId(selected)}
            onChange={(_, nextTags) => patchValue({ tagIds: nextTags.map(tag => String(normalizeId(tag))) })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Selecione as tags"
              />
            )}
            renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={normalizeId(option)}
                label={option.name}
                size="small"
              />
            ))}
          />

          <Box sx={{ gridColumn: { xs: 'auto', md: compact ? 'auto' : '1 / -1' }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${filteredArticles.length} de ${articles.length} artigos no envio`} color="primary" variant="outlined" />
            {removedCount > 0 && (
              <Chip size="small" label={`${removedCount} removido(s) pelo filtro`} color="warning" variant="outlined" />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

NewsletterTagFilterControl.propTypes = {
  articles: PropTypes.array,
  availableTags: PropTypes.array,
  value: PropTypes.shape({
    enabled: PropTypes.bool,
    mode: PropTypes.oneOf(['exclude', 'include']),
    tagIds: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  tagsOrder: PropTypes.arrayOf(PropTypes.string)
};

export default NewsletterTagFilterControl;
